// useBibliographyEntries — loads source-type references for the active Piece,
// joined with their My Library rows. Exposes toggle (cited flag) and remove
// mutations that reuse the existing composer-core functions, then refetches.
import { useCallback, useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import {
  deleteBriefReference,
  updateBriefReferenceCited,
} from "@/lib/composer/core/pieces/references";
import type { LibraryItem } from "@/lib/library";

export type BibliographyEntry = {
  /** content_piece_references.id */
  id: string;
  /** library_items.id */
  referenceId: string;
  cited: boolean;
  labelSnapshot: string | null;
  sortOrder: number;
  item: LibraryItem | null;
};

export const useBibliographyEntries = (
  workspaceId: string | null,
  pieceId: string | null,
) => {
  const [entries, setEntries] = useState<BibliographyEntry[]>([]);
  const [loading, setLoading] = useState(false);
  const [version, setVersion] = useState(0);

  const refetch = useCallback(() => setVersion((v) => v + 1), []);

  useEffect(() => {
    if (!workspaceId || !pieceId) {
      setEntries([]);
      return;
    }
    let cancelled = false;
    setLoading(true);
    (async () => {
      const { data: refs, error: refErr } = await supabase
        .from("content_piece_references")
        .select("id, reference_id, cited, label_snapshot, sort_order")
        .eq("workspace_id", workspaceId)
        .eq("piece_id", pieceId)
        .eq("reference_type", "source")
        .order("sort_order", { ascending: true });

      if (cancelled) return;
      if (refErr) {
        toast.error(refErr.message);
        setEntries([]);
        setLoading(false);
        return;
      }

      const rows = (refs ?? []) as Array<{
        id: string;
        reference_id: string;
        cited: boolean | null;
        label_snapshot: string | null;
        sort_order: number | null;
      }>;

      const ids = Array.from(new Set(rows.map((r) => r.reference_id)));
      let itemMap = new Map<string, LibraryItem>();
      if (ids.length > 0) {
        const { data: items, error: itemErr } = await supabase
          .from("library_items")
          .select("*")
          .in("id", ids);
        if (cancelled) return;
        if (itemErr) {
          toast.error(itemErr.message);
        } else {
          itemMap = new Map(
            ((items ?? []) as LibraryItem[]).map((it) => [it.id, it]),
          );
        }
      }

      setEntries(
        rows.map((r) => ({
          id: r.id,
          referenceId: r.reference_id,
          cited: r.cited !== false,
          labelSnapshot: r.label_snapshot,
          sortOrder: r.sort_order ?? 0,
          item: itemMap.get(r.reference_id) ?? null,
        })),
      );
      setLoading(false);
    })();
    return () => {
      cancelled = true;
    };
  }, [workspaceId, pieceId, version]);

  const toggleCited = useCallback(
    async (id: string, next: boolean) => {
      if (!workspaceId) return;
      // Optimistic update.
      setEntries((prev) =>
        prev.map((e) => (e.id === id ? { ...e, cited: next } : e)),
      );
      try {
        await updateBriefReferenceCited(workspaceId, id, next);
      } catch (err) {
        toast.error((err as Error).message);
        refetch();
      }
    },
    [workspaceId, refetch],
  );

  const remove = useCallback(
    async (id: string) => {
      if (!workspaceId) return;
      const prev = entries;
      setEntries((curr) => curr.filter((e) => e.id !== id));
      try {
        await deleteBriefReference(workspaceId, id);
      } catch (err) {
        toast.error((err as Error).message);
        setEntries(prev);
      }
    },
    [workspaceId, entries],
  );

  return { entries, loading, refetch, toggleCited, remove };
};
